import {
    bindFirst,
    createSlatePlugin,
    createTSlatePlugin,
    type PluginConfig,
} from '@udecode/plate';
import {
    type TriggerComboboxPluginOptions,
    withTriggerCombobox,
} from '@udecode/plate-combobox';

import { getCurrentCitationElement } from './api';
import type { TCitationElement } from './types';
  
export type BaseCitationConfig = PluginConfig<
    'citation',
    {
        insertSpaceAfterCitation?: boolean;
    } & TriggerComboboxPluginOptions,
    {
        citation: {
            getCurrentElement: () => TCitationElement | null;
        }
    },
    {
        insert: {
            citation: (options: { search: string; value: any; key?: any }) => void;
            citationInput: (options: { search: string; value: any }) => void;
        };
    }
  >;
  
export const BaseCitationInputPlugin = createSlatePlugin({
    key: 'citation_input',
    node: { isElement: true, isInline: true, isVoid: true },
});
  
/** Enables support for autocompleting @citations. */
export const BaseCitationPlugin = createTSlatePlugin<BaseCitationConfig>({
    key: 'citation',
    node: { isElement: true, isInline: true, isMarkableVoid: true, isVoid: true },
    options: {
        trigger: '@',
        triggerPreviousCharPattern: /^\s?$/,
        createComboboxInput: (trigger) => ({
            children: [{ text: '' }],
            trigger,
            type: BaseCitationInputPlugin.key,
        }),
    },
    plugins: [BaseCitationInputPlugin],
})
    .extendEditorApi<Partial<BaseCitationConfig['api']>>(({ editor }) => ({
        citation: {
            getCurrentElement: bindFirst(getCurrentCitationElement, editor),
        }
    }))
    .extendEditorTransforms<BaseCitationConfig['transforms']>(({ editor, type }) => ({
        insert: {
            citation: ({ key, value }) => {
                editor.tf.insertNodes<TCitationElement>({
                    key,
                    children: [{ text: '' }],
                    type,
                    ref: value,
                });
            },
            citationInput: ({ search, value }) => {
                editor.tf.insertNodes({
                    type: BaseCitationInputPlugin.key,
                    children: [{ text: search }],
                    ref: value,
                });
            }
        },
    }))
    .overrideEditor(withTriggerCombobox as any);
